import { useEffect } from 'react';
import {
  resolveThemeForMode,
  themeDeclaresSurfaces,
  themeToCssVars,
  type ThemeTokens,
} from '@mlm/shared';

/**
 * Apply a tenant's theme tokens as inline CSS custom properties on `<html>`.
 *
 * Inline element styles outrank ANY stylesheet rule, so the tenant theme
 * overrides each app's baseline CSS without those files being edited, and a
 * token the tenant omits is simply never set — an un-themed deployment looks
 * exactly like the demo.
 *
 * This is the SHARED half: no queries, no app-specific fallbacks. Each SPA
 * wraps it with its own `['tenant-config']` read, its own mode source and its
 * own favicon precedence, then hands the results in. The member portal has done
 * exactly this since white-labelling shipped; the creator portal joined it when
 * Creator Studio became a themed surface.
 *
 * DARK MODE: because these vars are inline on `<html>` they outrank the `.dark`
 * block in `@mlm/ui`, so the mode is applied HERE by re-resolving the token set.
 * Toggling the class alone would not recolor a themed tenant.
 *
 * SURFACES: `tenant-surfaces` goes on `<html>` when the tenant overrides any
 * surface token. The member storefront's own scoped surface block keys off it —
 * a descendant selector would otherwise beat the tenant's `:root` values.
 */
export function useApplyTenantTheme({
  theme,
  mode = 'light',
  faviconUrl,
}: {
  theme: ThemeTokens | null | undefined;
  /** Callers with no dark set of their own pass nothing and stay light. */
  mode?: 'light' | 'dark';
  /** Already resolved by the caller (hostname override, uploaded key, URL…). */
  faviconUrl?: string | null;
}) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', mode === 'dark');
    root.classList.toggle('tenant-surfaces', themeDeclaresSurfaces(theme));
    const vars = themeToCssVars(resolveThemeForMode(theme, mode));
    for (const [name, value] of vars) root.style.setProperty(name, value);

    if (faviconUrl) {
      const icon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (icon) icon.href = faviconUrl;
    }

    // Load the tenant's font stylesheets (Google Fonts etc.) so the `--font-*`
    // vars resolve. Each app's baseline <link>s in index.html stay as the
    // un-themed fallback; these are ADDED on top for a themed tenant.
    const links: HTMLLinkElement[] = [];
    for (const href of theme?.fontLinks ?? []) {
      if (typeof href !== 'string' || href.trim() === '') continue;
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      el.dataset.tenantFont = '';
      document.head.appendChild(el);
      links.push(el);
    }

    return () => {
      for (const [name] of vars) root.style.removeProperty(name);
      for (const el of links) el.remove();
    };
  }, [theme, mode, faviconUrl]);
}
